import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = null;
      }

      if (response.ok) {
        toast.success("Message sent successfully! We'll get back to you within 24 hours.", { duration: 6000 });
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 10000);
        return;
      }

      let errorMessage = 'Failed to send message. Please try again.';
      
      if (response.status === 429) {
        errorMessage = responseData?.detail || 'Too many requests. Please wait a few minutes and try again.';
      } else if (response.status === 422 && responseData) {
        if (Array.isArray(responseData.detail)) {
          const errors = responseData.detail.map(err => {
            const fieldName = err.field.charAt(0).toUpperCase() + err.field.slice(1);
            return `${fieldName}: ${err.message}`;
          });
          errorMessage = errors.join('. ');
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        }
      } else if (responseData && responseData.detail) {
        errorMessage = responseData.detail;
      }
      
      toast.error(errorMessage, { duration: 8000 });
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 10000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      let errorMsg = 'Failed to send message. ';
      if (error.message.includes('fetch') || error.message.includes('network')) {
        errorMsg += 'Network error - please check your internet connection.';
      } else {
        errorMsg += 'Please try again or call us at 07747075809.';
      }
      
      toast.error(errorMsg, { duration: 6000 });
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO 
        title="Contact Us - Book Your Physiotherapy Appointment in Harrow | MSK and Sports Physio"
        description="Contact MSK and Sports Physio in Harrow. Call 07747075809 or email mskandsportsphysio@gmail.com. Located at HarroWall Climbing Centre. Book your physiotherapy assessment today!"
        keywords="contact physiotherapist Harrow, book physio appointment Harrow, physiotherapy clinic contact, HarroWall Climbing Centre physio"
        type="website"
      />
      {/* Hero Section */}
      <section className="py-10 md:py-20 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge className="bg-[#dbeafe] text-[#2563eb] hover:bg-[#dbeafe] mb-4">Contact Us</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            Get in Touch
          </h1>
          <p className="text-base md:text-xl text-gray-600 leading-relaxed">
            Have a question or ready to book an appointment? We're here to help you start your recovery journey.
          </p>
        </div>
      </section>

      {/* Contact Information Cards - Desktop only (mobile shows form first) */}
      <section className="hidden md:block py-12 px-4 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-gray-100 hover:border-[#dbeafe] hover:shadow-lg transition-all duration-300">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-[#dbeafe] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a 
                  href={`tel:${mockData.business.phone}`}
                  className="text-[#2563eb] hover:text-[#2563eb] transition-colors duration-200"
                >
                  {mockData.business.phone}
                </a>
                <p className="text-xs text-gray-500 mt-2">Mon-Fri 9am-7pm</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#dbeafe] hover:shadow-lg transition-all duration-300">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-[#dbeafe] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a 
                  href={`mailto:${mockData.business.email}`}
                  className="text-[#2563eb] hover:text-[#2563eb] transition-colors duration-200 text-sm break-all"
                >
                  {mockData.business.email}
                </a>
                <p className="text-xs text-gray-500 mt-2">24-hour response</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#dbeafe] hover:shadow-lg transition-all duration-300">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-[#dbeafe] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                <p className="text-gray-600 text-sm">{mockData.business.address}</p>
                <p className="text-xs text-gray-500 mt-2">Harrow area</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#dbeafe] hover:shadow-lg transition-all duration-300">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-[#dbeafe] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Hours</h3>
                <p className="text-gray-600 text-sm">{mockData.business.hours}</p>
                <p className="text-xs text-gray-500 mt-2">Flexible scheduling</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-10 md:py-20 px-4 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Info (desktop order 1, mobile order 2) */}
            <div className="order-2 lg:order-1">
              {/* Heading hidden on mobile - the form card already shows "Send Us a Message" */}
              <h2 className="hidden lg:block text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <p className="hidden lg:block text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're looking to book an appointment, have questions about our services, or need advice about your condition, we're here to help.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Phone className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call Us Directly</h3>
                    <p className="text-gray-600 text-sm mb-2">
                      For urgent enquiries or immediate booking, give us a call.
                    </p>
                    <a 
                      href={`tel:${mockData.business.phone}`}
                      className="text-[#2563eb] font-semibold hover:text-[#2563eb]"
                    >
                      {mockData.business.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Mail className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Enquiries</h3>
                    <p className="text-gray-600 text-sm mb-2">
                      Send us an email and we'll respond within 24 hours.
                    </p>
                    <a 
                      href={`mailto:${mockData.business.email}`}
                      className="text-[#2563eb] font-semibold hover:text-[#2563eb] break-all"
                    >
                      {mockData.business.email}
                    </a>
                  </div>
                </div>
              </div>

              <Card className="bg-[#eff6ff] border-0">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">What to Expect</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#2563eb] mt-1">•</span>
                      <span>Response within 24 hours on weekdays</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#2563eb] mt-1">•</span>
                      <span>Appointment confirmation via email or phone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#2563eb] mt-1">•</span>
                      <span>Pre-appointment forms sent before your visit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#2563eb] mt-1">•</span>
                      <span>Friendly, professional service</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Form (desktop order 2, mobile order 1) */}
            <Card className="order-1 lg:order-2 border-2 border-gray-100 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <CardDescription>Fill out the form below and we'll get back to you soon</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-green-800 font-semibold">Message Sent Successfully!</h4>
                        <p className="text-green-700 text-sm mt-1">Thank you for your message. We'll get back to you within 24 hours.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-red-800 font-semibold">Unable to Send Message</h4>
                        <p className="text-red-700 text-sm mt-1">Please try again or contact us directly at 07747075809</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="h-11"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="h-11"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="7700900000 or 07700900000"
                      className="h-11"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter UK phone number (with or without leading 0)</p>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="e.g., Appointment Booking, General Enquiry"
                      className="h-11"
                    />
                    <p className={`text-xs mt-1 ${formData.subject.length < 5 && formData.subject.length > 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      {formData.subject.length < 5 && formData.subject.length > 0 
                        ? `⚠️ Too short! Need ${5 - formData.subject.length} more character${5 - formData.subject.length === 1 ? '' : 's'} (minimum 5)`
                        : `${formData.subject.length}/5 characters minimum`
                      }
                    </p>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your injury, condition, or enquiry..."
                      rows={6}
                      className="resize-none"
                    />
                    <p className={`text-xs mt-1 ${formData.message.length < 10 && formData.message.length > 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      {formData.message.length < 10 && formData.message.length > 0
                        ? `⚠️ Too short! Need ${10 - formData.message.length} more character${10 - formData.message.length === 1 ? '' : 's'} (minimum 10)`
                        : `${formData.message.length}/10 characters minimum`
                      }
                    </p>
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting || formData.subject.length < 5 || formData.message.length < 10}
                    className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-12 text-base font-semibold rounded-lg shadow-lg shadow-[#2563eb]/30 hover:shadow-xl hover:shadow-[#2563eb]/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : formData.subject.length < 5 || formData.message.length < 10 ? (
                      <>
                        <span className="mr-2">⚠️</span>
                        Complete All Required Fields
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  {(formData.subject.length > 0 && formData.subject.length < 5) || (formData.message.length > 0 && formData.message.length < 10) ? (
                    <p className="text-sm text-red-600 text-center mt-2 font-semibold">
                      ⚠️ Please complete the form requirements above before submitting
                    </p>
                  ) : null}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Find Us</h2>
            <p className="text-lg text-gray-600">Located inside HarroWall Climbing Centre</p>
          </div>
          
          {/* Clickable Map Wrapper */}
          <div 
            className="cursor-pointer group relative"
            onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=HarroWall+Climbing+Centre,+Neptune+Trading+Estate,+Neptune+Rd,+Harrow+HA1+4HX,+UK', '_blank')}
          >
            <Card className="overflow-hidden border-0 shadow-lg transition-all duration-300 group-hover:shadow-2xl">
              {/* Overlay hint */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 z-10 flex items-center justify-center">
                <div className="bg-white/95 px-6 py-3 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-[#2563eb] font-semibold flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Click to open in Google Maps
                  </p>
                </div>
              </div>
              
              {/* Google Maps Embed - MSK and Sports Physio at HarroWall */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2476.7995864922785!2d-0.35769!3d51.5941!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761391c88e0e0d%3A0x4d4a67e8b0e8a8b8!2sHarroWall%20Climbing%20Centre!5e0!3m2!1sen!2suk!4v1704711600000!5m2!1sen!2suk"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MSK and Sports Physio - HarroWall Climbing Centre"
                className="pointer-events-none"
              />
            </Card>
          </div>
          
          <div className="text-center mt-8">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-3">MSK and Sports Physio</h3>
              <p className="text-gray-700 font-semibold mb-2">📍 Located inside HarroWall Climbing Centre</p>
              <p className="text-gray-600 mb-4">Neptune Trading Estate, Neptune Rd, Harrow HA1 4HX</p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=HarroWall+Climbing+Centre,+Neptune+Trading+Estate,+Neptune+Rd,+Harrow+HA1+4HX,+UK', '_blank')}
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                >
                  <MapPin className="mr-2 w-4 h-4" />
                  Get Directions
                </Button>
                
                <Button 
                  onClick={() => window.open('https://www.google.com/maps/search/?api=1&query=HarroWall+Climbing+Centre,+Neptune+Rd,+Harrow+HA1+4HX', '_blank')}
                  variant="outline"
                  className="border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#eff6ff]"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  View on Google Maps
                </Button>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                💡 Tip: Click anywhere on the map above to open Google Maps for directions
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
